// UI genérica para formularios Patrón B "de campos" — ver `camposSimplesDatos.ts`.
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { leerSiNo, type CampoSimple, type SiNoValor, type Valores } from './camposSimplesDatos';

export function Bloque({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{titulo}</h4>
      {children}
    </section>
  );
}

const TIPO_INPUT = { texto: 'text', numero: 'number', numeroNA: 'text', fecha: 'date', hora: 'time', select: 'text', casilla: 'text', area: 'text', siNo: 'text' } as const;

export function CamposSimples({ prefijoId, campos, valores, onChange, columnas = 3 }: { readonly prefijoId: string; readonly campos: readonly CampoSimple[]; readonly valores: Valores; readonly onChange: (v: Valores) => void; readonly columnas?: 2 | 3 | 4 }): JSX.Element {
  const { t } = useTranslation();
  const clases = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[columnas];
  return (
    <div className={`grid grid-cols-1 gap-3 ${clases}`}>
      {campos.map((c) => {
        const id = `${prefijoId}-${c.clave}`;
        const valor = c.calculado ? c.calculado(valores) : (valores[c.clave] ?? '');
        const cambiar = (v: string): void => onChange({ ...valores, [c.clave]: v });
        if (c.tipo === 'select') return <SelectSimple key={c.clave} id={id} etiqueta={c.etiqueta} valor={valor} opciones={c.opciones ?? []} onChange={cambiar} />;
        if (c.tipo === 'siNo') return <SiNoBotones key={c.clave} id={id} etiqueta={c.etiqueta} valor={leerSiNo(valor)} onChange={cambiar} />;
        if (c.tipo === 'casilla') {
          return (
            <div key={c.clave} className="flex items-center gap-2 self-end pb-2">
              <Checkbox id={id} checked={valor === 'true'} onCheckedChange={(v) => cambiar(v === true ? 'true' : '')} />
              <Label htmlFor={id} className="font-normal">
                {c.etiqueta}
              </Label>
            </div>
          );
        }
        if (c.tipo === 'area') {
          return (
            <div key={c.clave} className="sm:col-span-full">
              <AreaDictado id={id} etiqueta={c.etiqueta} valor={valor} onChange={cambiar} filas={3} />
            </div>
          );
        }
        if ((c.tipo ?? 'texto') === 'texto') {
          return <CampoDictado key={c.clave} id={id} etiqueta={c.etiqueta} valor={valor} onChange={cambiar} soloLectura={c.calculado !== undefined || c.soloLectura} />;
        }
        return (
          <div key={c.clave} className="flex flex-col gap-1">
            <Label htmlFor={id}>{c.etiqueta}</Label>
            <Input id={id} type={c.calculado ? 'text' : TIPO_INPUT[c.tipo ?? 'texto']} inputMode={c.tipo === 'numeroNA' ? 'decimal' : undefined} placeholder={c.tipo === 'numeroNA' && !c.calculado ? t('formulario.placeholderNA') : undefined} value={valor} min={c.min} max={c.max} step={c.min !== undefined || c.max !== undefined ? 1 : undefined} readOnly={c.calculado !== undefined || c.soloLectura} onChange={(e) => cambiar(e.target.value)} />
          </div>
        );
      })}
    </div>
  );
}

export function SelectSimple({ id, etiqueta, valor, opciones, onChange }: { readonly id: string; readonly etiqueta: string; readonly valor: string; readonly opciones: readonly { readonly valor: string; readonly etiqueta: string }[]; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Select value={valor || undefined} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((o) => (
            <SelectItem key={o.valor} value={o.valor}>
              {o.etiqueta}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Par de botones Sí / No con estado "sin responder": volver a pulsar el elegido lo limpia. */
export function SiNoBotones({ id, etiqueta, valor, onChange, textos = { si: 'Sí', no: 'No' } }: { readonly id: string; readonly etiqueta: string; readonly valor: SiNoValor; readonly onChange: (v: SiNoValor) => void; readonly textos?: { readonly si: string; readonly no: string } }): JSX.Element {
  const boton = (v: 'si' | 'no', texto: string): JSX.Element => (
    <Button type="button" size="sm" variant={valor === v ? 'default' : 'outline'} aria-pressed={valor === v} onClick={() => onChange(valor === v ? '' : v)}>
      {texto}
    </Button>
  );
  return (
    <fieldset id={id} className="flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="p-0 text-sm font-medium">{etiqueta}</legend>
      <div className="flex gap-2">
        {boton('si', textos.si)}
        {boton('no', textos.no)}
      </div>
    </fieldset>
  );
}
